import React, { Key, useEffect, useState } from 'react';
import type { ColumnsType } from 'antd/es/table';
import {
  ButtonCommon,
  ColumnSelector,
  ExcelOperationsHandler,
  IconSort,
  SearchByFields,
  SkeletonTable,
  TableCommon,
  TopHeader,
} from 'components/common';
import { Columns, ListItemKPI, QueryParams } from 'types';
import { Kanban, Plus } from '@phosphor-icons/react';
import { useGetAllInventoryCheckOrdersQuery } from 'services';
import { InventoryDocument } from 'models';
import { useNavigate } from 'react-router-dom';
import { ReturnOrderRecord } from 'types/common';
import StatusBadgeCommon from 'components/common/Badge/StatusBadgeCommon';
import { buildFullParams } from 'utils/filterBuilder';
import SelectReturnOption from 'components/ReturnOrder/SelectReturnOption';
const ReturnOrderListPage: React.FC = () => {
  const navigate = useNavigate();
  const [returnOrders, setReturnOrders] = useState<ReturnOrderRecord[]>([]);
  const params = new URLSearchParams(window.location.search);
  const isPreviewMode = params.get('preview') === 'true';
  const [isOpen, setIsOpen] = useState(false);
  const [filteredQuery, setFilteredQuery] = useState<QueryParams>({
    include: 'status|createdBy.baseUser|inventoryDocumentItems|supplier.baseUser',
    size: 10,
    page: 1,
    sort: 'updatedAt:desc',
    filter: 'type:eq:return-purchase-order',
  });
  const {
    data: returnOrderData,
    isLoading,
    // isFetching,
  } = useGetAllInventoryCheckOrdersQuery({
    query: filteredQuery,
  });

  const [kips, setKips] = useState<{
    rpoDraft: number;
    rpoPaid: number;
  }>({
    rpoDraft: 0,
    rpoPaid: 0,
  });
  useEffect(() => {
    if (returnOrderData?.data?.rows) {
      const mappedReturnOrders: ReturnOrderRecord[] = returnOrderData.data.rows.map(
        (returnOrder: InventoryDocument) => ({
          key: returnOrder.id,
          id: returnOrder.id,
          code: returnOrder.code || '',
          'supplier.baseUser.name': returnOrder.supplier?.baseUser?.name || '',
          createdAt: returnOrder.createdAt || new Date(),
          'status.code': returnOrder.status?.code || '',
          count: returnOrder.inventoryDocumentItems?.reduce((acc, item) => acc + Number(item.quantity || 0), 0) || 0,
          deliveryDate: returnOrder.deliveryDate?.toString() || '',
        }),
      );
      setReturnOrders(mappedReturnOrders);
    }
    if (returnOrderData?.data?.kpis) {
      setKips({
        rpoDraft: returnOrderData.data.kpis.rpoDraft || 0,
        rpoPaid: returnOrderData.data.kpis.rpoPaid || 0,
      });
    }
  }, [returnOrderData]);

  const returnOrderColumns: ColumnsType<ReturnOrderRecord> = [
    {
      title: 'MÃ ĐƠN',
      dataIndex: 'code',
      key: 'code',
      sorter: (a, b) => a.code.localeCompare(b.code),
      showSorterTooltip: false,
      filterSearch: true,
      sortIcon(props) {
        return <IconSort props={props} />;
      },
      render: (text) => (
        <div className='font-mono' style={{ fontWeight: 600, color: 'var(--textBlack)' }}>
          {text}
        </div>
      ),
    },
    {
      title: 'NHÀ CUNG CẤP',
      dataIndex: 'supplier.baseUser.name',
      key: 'supplier.baseUser.name',
      sorter: (a, b) => a['supplier.baseUser.name'].localeCompare(b['supplier.baseUser.name']),
      showSorterTooltip: false,
      sortIcon(props) {
        return <IconSort props={props} />;
      },
      filterSearch: true,
    },
    {
      title: 'THỜI GIAN TẠO',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      showSorterTooltip: false,
      sortIcon(props) {
        return <IconSort props={props} />;
      },
      render: (date) => {
        const formattedDate = new Date(date).toLocaleDateString('vi-VN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        });
        return <div className='font-mono'>{formattedDate}</div>;
      },
      filterSearch: false,
    },
    {
      title: 'THỜI GIAN TRẢ ',
      dataIndex: 'deliveryDate',
      key: 'deliveryDate',
      sorter: (a, b) => new Date(a.deliveryDate).getTime() - new Date(b.deliveryDate).getTime(),
      showSorterTooltip: false,
      sortIcon(props) {
        return <IconSort props={props} />;
      },
      render: (date) => {
        const formattedDate = new Date(date).toLocaleDateString('vi-VN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        });
        return <div className='font-mono'>{formattedDate}</div>;
      },
      filterSearch: false,
    },
    {
      title: 'SỐ LƯỢNG',
      dataIndex: 'count',
      key: 'count',
      sorter: (a, b) => a.count - b.count,
      showSorterTooltip: false,
      sortIcon(props) {
        return <IconSort props={props} />;
      },
      render: (text: number) => <div>{text} sản phẩm</div>,
      filterSearch: false,
    },
    {
      title: 'TRẠNG THÁI',
      dataIndex: 'status.code',
      key: 'status.code',
      sorter: (a, b) => a['status.code'].localeCompare(b['status.code']),
      showSorterTooltip: false,
      sortIcon(props) {
        return <IconSort props={props} />;
      },
      filterSearch: true,
      filters: [
        { text: 'Bản nháp', value: 'rpo-draft' },
        { text: 'Đã tạo đơn', value: 'rpo-paid ' },
      ],
      render: (statusCode) => {
        return (
          <StatusBadgeCommon
            label={statusCode === 'rpo-paid' ? 'Đã tạo đơn' : 'Bản nháp'}
            bgColorClass={statusCode === 'rpo-paid' ? 'bg-[var(--sematicSuccessColor500)]' : 'bg-[--textBlack56]'}
          />
        );
      },
    },
  ];

  const listItemTopHeader: ListItemKPI[] = [
    {
      icon: <Kanban size={32} />,
      value: (kips.rpoDraft + kips.rpoPaid).toString() || '0',
      description: 'Tổng số lượng đơn trả ',
    },
    {
      icon: <Kanban size={32} />,
      value: kips.rpoPaid.toString() || '0',
      description: `Đơn đã hoàn tất`,
      iconDescription: (
        <svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10' fill='none'>
          <circle cx='5' cy='5' r='5' fill='#31A168' />
        </svg>
      ),
    },
    {
      icon: <Kanban size={32} />,
      value: kips.rpoDraft.toString() || '0',
      description: 'Bản nháp',
      iconDescription: (
        <svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10' fill='none'>
          <circle cx='5' cy='5' r='5' fill='black' fillOpacity='0.56' />
        </svg>
      ),
    },
  ];

  const returnOrderColumnsCheck: Columns[] = returnOrderColumns.map((column) => ({
    key: column.key as string,
    title: column.title as string,
  }));

  const [checkedList, setCheckedList] = useState<(Key | undefined)[]>(returnOrderColumns.map((column) => column.key));

  const getFilteredColumns = () => {
    return returnOrderColumns.map((item) => ({
      ...item,
      hidden: !checkedList.includes(item.key),
    }));
  };

  const handlePagination = (query: QueryParams) => {
    const columnArray = ['status.code'];
    const columnEqs = ['type'];
    const filterQuery = buildFullParams(
      {
        ...query,
        type: 'return-purchase-order',
      },
      columnEqs,
      columnArray,
    );
    setFilteredQuery((prev) => ({
      ...prev,
      ...filterQuery,
    }));
  };
  const handleView = (record: ReturnOrderRecord) => {
    if (record['status.code'] === 'rpo-paid') {
      navigate(`/management/inventory/return-orders/detail/${record.id}`);
    } else {
      navigate(`/management/inventory/return-orders/create?id=${record.id}`);
    }
  };

  return (
    <div className='flex flex-col h-full relative py-2'>
      {!isPreviewMode && (
        <TopHeader
          className='mb-4 rounded-lg'
          title='DANH SÁCH PHIẾU TRẢ HÀNG'
          subTitle='Tất cả các phiếu trả hàng được hiển thị tại đây'
          listItem={listItemTopHeader}
        />
      )}
      <div className='w-full bg-[var(--textWhite)]'>
        <div className='flex lg:flex-row flex-col gap-2 justify-between items-center px-[24px] py-[12px] flex-wrap gap-2 lg:gap-0'>
          <h1 className='text-[18px] leading-[26px] font-[700]'>DANH SÁCH PHIẾU TRẢ HÀNG</h1>
          <div className='flex gap-2'>
            <div className='flex-1 max-w-[250px]'>
              <SearchByFields
                filteredQuery={filteredQuery}
                setFilteredQuery={(query) => setFilteredQuery({ ...query, page: 1 } as unknown as QueryParams)}
                searchFields={['code']}
                placeholder='Tìm kiếm theo tên hoặc mã...'
              />
            </div>
            <ColumnSelector
              allColumns={returnOrderColumnsCheck}
              selectedColumns={checkedList}
              onChange={(selected) => setCheckedList(selected)}
            />
            <ExcelOperationsHandler entityType='return-order' entityName='Phiếu Trả Hàng' disabled={false} />
            <ButtonCommon
              icon={<Plus size={20} />}
              color='primary'
              onClick={() => {
                setIsOpen(true);
              }}
              size='xs'
              radius='rounded'
            >
              Tạo phiếu trả mới
            </ButtonCommon>
          </div>
        </div>
      </div>

      <div className='flex-grow bg-[var(--textWhite)] flex flex-col'>
        <div className='flex-grow flex flex-col'>
          {isLoading ? (
            <SkeletonTable columns={returnOrderColumns.length} rows={10} hasHeader={true} />
          ) : (
            <TableCommon<ReturnOrderRecord>
              columns={getFilteredColumns()}
              dataSource={returnOrders}
              defaultPageSize={10}
              count={returnOrderData?.data?.count || 0}
              onView={handleView}
              displayView={true}
              onEdit={() => {}}
              tableProps={{
                scroll: { x: 'max-content' },
                size: 'large',
                className: 'h-full',
                style: {
                  minHeight: !isPreviewMode ? 'calc(100vh - 275px)' : '100vh',
                  display: 'flex',
                  flexDirection: 'column',
                },
                loading: false,
              }}
              onPaginationChange={handlePagination}
            />
          )}
        </div>
      </div>
      <SelectReturnOption
        isOpen={isOpen}
        onCancel={function (): void {
          setIsOpen(false);
        }}
        onConfirm={function (id: string): void {
          if (id && id !== '') {
            navigate(`/management/inventory/return-orders/create?orderId=${id}`);
          } else {
            navigate(`/management/inventory/return-orders/create`);
          }
          setIsOpen(false);
        }}
      />
    </div>
  );
};

export default ReturnOrderListPage;
